import { useEffect, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { paths } from '../../app/routes'
import { Dialog } from '../../components/ui/Dialog'
import { Button } from '../../components/ui/Button'
import { faNumber } from '../../lib/faNumbers'
import { rejectReasonText } from '../../lib/rejectReasonText'
import { db } from '../../outbox/db'
import type { OutboxOp } from '../../outbox/types'
import { settleBasketAssignment } from '../../outbox/operations'
import { readAssignResultState } from './assignState'
import styles from './AssignResultScreen.module.css'

interface ResultRow {
  opId: string
  serialNumber: string
  status: 'accepted' | 'rejected'
  reason: Parameters<typeof rejectReasonText>[0]
  currentPalletCode: string | null
}

export function AssignResultScreen() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const routeState = readAssignResultState(state)
  const routeKey = routeState?.opIds.join('|') ?? ''
  const operations =
    useLiveQuery(
      () =>
        routeState === null
          ? Promise.resolve<OutboxOp[]>([])
          : db.outbox.bulkGet(routeState.opIds).then((rows) =>
              rows.filter((operation): operation is OutboxOp => operation !== undefined),
            ),
      [routeKey],
      [],
    ) ?? []
  const [capacityDialogOpen, setCapacityDialogOpen] = useState(false)
  const capacityDialogKey = useRef<string | null>(null)
  const settledRoute = useRef<string | null>(null)

  const resultRows = operations.flatMap<ResultRow>((operation) =>
    (operation.result?.results ?? []).map((result) => ({
      opId: operation.id,
      serialNumber: result.serialNumber,
      status: result.status,
      reason: result.reason ?? null,
      currentPalletCode: result.currentPalletCode ?? null,
    })),
  )
  const rejectedRows = resultRows.filter((row) => row.status === 'rejected')
  const total = routeState?.opIds.length ?? 0
  const accepted = resultRows.filter((row) => row.status === 'accepted').length
  const rejected = rejectedRows.length
  const pending = Math.max(0, total - accepted - rejected)
  const isSettled =
    routeState !== null &&
    operations.length === routeState.opIds.length &&
    operations.every(
      (operation) =>
        operation.result !== undefined &&
        (operation.status === 'done' || operation.status === 'rejected'),
    )
  const capacityRows = rejectedRows.filter((row) => row.reason === 'pallet_full')
  const capacityKey =
    capacityRows.length === 0
      ? null
      : capacityRows.map((row) => `${row.opId}:${row.serialNumber}`).join('|')

  useEffect(() => {
    if (routeState === null || !isSettled || settledRoute.current === routeKey) {
      return
    }

    settledRoute.current = routeKey
    void settleBasketAssignment(routeState.basketEntries)
  }, [isSettled, routeKey, routeState])

  useEffect(() => {
    if (capacityKey === null || capacityDialogKey.current === capacityKey) {
      return
    }

    capacityDialogKey.current = capacityKey
    setCapacityDialogOpen(true)
  }, [capacityKey])

  if (routeState === null) {
    return (
      <div className={styles.screen}>
        <div className={styles.empty}>نتیجه‌ای برای نمایش وجود ندارد.</div>
        <div className={styles.footer}>
          <Button compact onClick={() => navigate(paths.basket)}>
            بازگشت به سبد
          </Button>
        </div>
      </div>
    )
  }

  const statusPending = pending > 0

  return (
    <div className={styles.screen}>
      <div className={`${styles.status} ${statusPending ? styles.statusPending : ''}`}>
        <div className={styles.statusTitle}>
          {statusPending ? '✓ روی دستگاه ذخیره شد · منتظر شبکه' : 'تخصیص انجام شد'}
        </div>
        <div className={styles.palletCode} dir="ltr">
          {routeState.palletCode}
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.statRow}>
          <span className={styles.statLabel}>ارسال‌شده</span>
          <span className={styles.statValue} dir="ltr">
            {faNumber(total)}
          </span>
        </div>
        <div className={styles.statRow}>
          <span className={styles.statAccepted}>پذیرفته‌شده</span>
          <span className={styles.statValue} dir="ltr">
            {faNumber(accepted)}
          </span>
        </div>
        <div className={styles.statRow}>
          <span className={styles.statRejected}>رد‌شده</span>
          <span className={styles.statValue} dir="ltr">
            {faNumber(rejected)}
          </span>
        </div>
      </div>

      {rejectedRows.length > 0 ? (
        <div className={styles.failed}>
          <div className={styles.failedTitle}>موارد رد‌شده</div>
          {rejectedRows.map((row) => (
            <div key={`${row.opId}:${row.serialNumber}`} className={styles.failedRow}>
              <span className={styles.failedSerial} dir="ltr">
                {row.serialNumber}
              </span>
              <span className={styles.failedReason}>{rejectReasonText(row.reason)}</span>
              {row.currentPalletCode !== null && (
                <span className={styles.failedPallet} dir="ltr">
                  {row.currentPalletCode}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          {statusPending ? 'پاسخ سرور برای سریال‌ها در راه است.' : 'همه سریال‌ها پذیرفته شدند.'}
        </div>
      )}

      <div className={styles.footer}>
        <Button
          variant="secondary"
          compact
          disabled={rejectedRows.length === 0}
          onClick={() => navigate(paths.queue)}
        >
          بررسی موارد
        </Button>
        <Button compact onClick={() => navigate(paths.home)}>
          پایان
        </Button>
      </div>

      {capacityDialogOpen && (
        <Dialog
          title="ظرفیت پالت پر شده است"
          tone="danger"
          onCancel={() => setCapacityDialogOpen(false)}
          onConfirm={() => {
            setCapacityDialogOpen(false)
            navigate(paths.queue)
          }}
          cancelLabel="بستن"
          confirmLabel="بررسی موارد"
        >
          <div>پاسخ سرور نشان می‌دهد ظرفیت پالت مقصد هنگام ثبت پر بوده است.</div>
        </Dialog>
      )}
    </div>
  )
}
